import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X, Loader2, Mail } from 'lucide-react';
import { Button } from './ui/button';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await axios.post(`${API}/transporters/verify-email`, { token });
        setStatus('success');
        setMessage(response.data.message);
        setUserId(response.data.user_id);
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. Please try again.');
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
        </div>

        <div className="max-w-lg mx-auto px-4 relative z-10">
          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-12 text-center">
            {status === 'verifying' && (
              <>
                <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-4">Verifying Your Email...</h1>
                <p className="text-gray-400">Please wait while we verify your email address.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                  <Check className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-4">Email Verified!</h1>
                <p className="text-gray-400 mb-6">{message}</p>
                
                {userId && (
                  <div className="bg-black/30 rounded-xl p-4 mb-6">
                    <p className="text-gray-400 text-sm mb-1">Your User ID:</p>
                    <p className="text-amber-500 font-mono text-xl font-bold">{userId}</p>
                  </div>
                )}

                <Button
                  onClick={() => navigate('/drivers/login')}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-4 rounded-full"
                >
                  Go to Login
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <X className="w-10 h-10 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-4">Verification Failed</h1>
                <p className="text-gray-400 mb-6">{message}</p>
                
                <div className="space-y-4">
                  <Button
                    onClick={() => {
                      const email = prompt('Enter your registered email to resend verification:');
                      if (email) {
                        axios.post(`${API}/transporters/resend-verification`, { email })
                          .then(() => alert('Verification email sent!'))
                          .catch(err => alert(err.response?.data?.detail || 'Failed to send email'));
                      }
                    }}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-4 rounded-full w-full"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Resend Verification Email
                  </Button>
                  <Button
                    onClick={() => navigate('/drivers')}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 px-8 py-4 rounded-full w-full"
                  >
                    Back to Registration
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EmailVerificationPage;
